import {
    Box,
    Dialog,
    Skeleton,
    styled,
    Typography,
    useTheme,
} from '@mui/material'
import { Button } from 'components'
import TreeNode from 'components/Filters/EidFilter/GenericFilter/TreeViewWithSearch/components/TreeWithSearch/TreeNode'
import { InputBox } from 'components/Popups/EditAssignments'
import { useTreeFilterAPI, useUpdateDirectAssignedLocations } from 'hooks'
import { useDebounce } from 'packages/core'
import { Icon } from 'packages/eid-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TREE_VIEW_LEVELS } from 'utils'

const AddAccessModalMain = styled(Dialog)(() => ({
    '& > .MuiBox-root': {
        overflowY: 'initial',
    },
    '& .MuiDialog-paper': {
        backgroundColor: '#f7f8fa',
        overflowY: 'initial',
        maxWidth: '44rem',
    },
}))

const NewAccessAssignmentModal = styled(Box)(() => ({
    '& h2': {
        fontSize: '2.4rem',
        color: '#1b1f1f',
        fontWeight: 'normal',
    },
}))

const AssignmentModalHeader = styled(Box)(() => ({
    padding: '3.2rem 4.0rem',
    '& svg': {
        top: '-1.4rem !important',
    },
}))

const AssignmentModalFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '3.2rem 4.0rem',
    gap: '1.6rem',

    '& button': {
        border: `solid 0.1rem ${theme.palette.primary.main}`,
        padding: '0.8rem 1.2rem',
        height: '3.2rem',
        fontSize: '1.4rem',
        '&:nth-of-type(1)': {
            background: theme.palette.primary.main,
            color: '#fff',
        },
        '&:nth-of-type(2)': {
            color: theme.palette.primary.main,
            maxWidth: '9.0rem',
        },
    },
}))

const NewDirectAssigned = styled(Box)(({ theme }) => ({
    padding: ' 0 4.0rem',
    borderBottom: '0.1rem solid #d8d8d8 !important',
}))

const SearchInput = styled(Box)(({ theme }) => ({
    '& input': {
        height: '4.0rem',
        marginTop: '1.2rem',
        fontSize: '1.6rem',
        '&::placeholder': {
            color: '#b4b4b4',
        },
    },
}))

const CloseIconBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    '& svg': {
        position: 'absolute',
        top: '-2.0rem',
        right: '-2.0rem',
        cursor: 'pointer',
    },
}))

const RoleBoxCardInner = styled(Box)(({ theme }) => ({
    maxHeight: '20.0rem',
    overflowY: 'auto',
    width: '100%',
    margin: '2.4rem 0',
}))

const MIN_CHARACTERS_REQUIRED_TO_SEARCH = 3

const AddDirectAssignedLocation = ({ resourceId, refetch }) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [searchedLocation, setSearchedLocation] = useState('')
    const [selectedLocation, setSelectedLocation] = useState(null)
    const debouncedSearchLocationValue = useDebounce(searchedLocation)

    const [openModal, setOpenModal] = useState(false)

    const { data: locationTree, isLoading } = useTreeFilterAPI(
        `/api/Lookups/getBusinessLocationTree?locationFor=1&levelsToLoad=${TREE_VIEW_LEVELS}`,
        undefined,
        undefined,
        debouncedSearchLocationValue?.length >=
            MIN_CHARACTERS_REQUIRED_TO_SEARCH
            ? debouncedSearchLocationValue
            : '',
        openModal,
    )

    const [updateDirectAssignedLocations, { isLoading: updatingLocation }] =
        useUpdateDirectAssignedLocations()

    const handleSelect = (id) => {
        setSelectedLocation(id)
    }
    const handleSave = () => {
        const dataObject = {
            locationsToAdd: [selectedLocation],
            resourceId: resourceId,
        }
        updateDirectAssignedLocations(dataObject).then((data) => {
            refetch()
            setOpenModal(false)
        })
    }
    return (
        <>
            <Button
                onClick={() => setOpenModal(true)}
                sx={{
                    borderRadius: '0.4rem',
                    padding: '1.2rem',
                    height: '4rem',
                    width: 'fit-content',
                    backgroundColor: theme.palette.primary.main,
                    color: '#ffffff',
                    fontSize: '1.4rem',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                    },
                }}
                startIcon={
                    <Icon
                        name={'Plus'}
                        fill="white"
                        style={{
                            width: '1.6rem',
                            height: '1.6rem',
                        }}
                    />
                }
            >
                {t('AddNewDirectAssignedLocation')}
            </Button>
            <AddAccessModalMain
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={t('')}
            >
                <NewAccessAssignmentModal>
                    <AssignmentModalHeader>
                        <CloseIconBox onClick={() => setOpenModal(false)}>
                            <Icon
                                name="Close"
                                width="1.6rem"
                                height="1.6rem"
                                color="#959598"
                            />
                        </CloseIconBox>
                        <Typography variant="h2">
                            {t('AddNewDirectAssignedLocation')}
                        </Typography>
                    </AssignmentModalHeader>
                    <NewDirectAssigned>
                        <SearchInput>
                            <InputBox>
                                <Typography variant="subtitle1">
                                    {t('SelectLocation')}
                                </Typography>
                                <input
                                    type="text"
                                    placeholder={t('SearchLocation')}
                                    value={searchedLocation}
                                    onChange={(e) =>
                                        setSearchedLocation(e.target.value)
                                    }
                                ></input>
                            </InputBox>
                        </SearchInput>
                        <Box>
                            <RoleBoxCardInner>
                                {isLoading ? (
                                    <Box>
                                        <Skeleton height={20} />
                                        <Skeleton height={20} />
                                    </Box>
                                ) : (
                                    <TreeNode
                                        handleSelect={(val) => {
                                            handleSelect(val?.id)
                                        }}
                                        explorer={locationTree || {}}
                                        value={selectedLocation}
                                        highlightText={searchedLocation}
                                        titleProp={'friendlyName'}
                                        valueProp={'id'}
                                        url={`/api/Lookups/getBusinessLocationTree?locationFor=1&levelsToLoad=${TREE_VIEW_LEVELS}`}
                                        showCollapseIcon={false}
                                        showRadioToSelect={false}
                                        disableAssignableCheck={true}
                                    />
                                )}
                            </RoleBoxCardInner>
                        </Box>
                    </NewDirectAssigned>
                    <AssignmentModalFooter>
                        <Button
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                            }}
                            onClick={() => handleSave()}
                            loading={updatingLocation}
                            disabled={!selectedLocation}
                            disabledStyles={{
                                backgroundColor: '#aab0b4 !important',
                                borderColor: '#aab0b4 !important',
                                color: '#ffffff !important',
                            }}
                        >
                            {t('AddNewDirectAssignedLocation')}
                        </Button>
                        <Button onClick={() => setOpenModal(false)}>
                            {t('Cancel')}
                        </Button>
                    </AssignmentModalFooter>
                </NewAccessAssignmentModal>
            </AddAccessModalMain>
        </>
    )
}

export default AddDirectAssignedLocation
